import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  TrendingUp,
  TrendingDown,
  Users,
  CreditCard,
  AlertTriangle,
  DollarSign,
  Activity,
  Shield,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral' | 'warning';
  description?: string;
  icon: React.ElementType;
  progress?: number;
  trend?: 'up' | 'down' | 'stable';
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  changeType = 'neutral',
  description,
  icon: Icon,
  progress,
  trend
}) => {
  const getChangeColor = () => {
    switch (changeType) {
      case 'positive': return 'text-green-500';
      case 'negative': return 'text-red-500';
      case 'warning': return 'text-yellow-500';
      default: return 'text-muted-foreground';
    }
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-3 w-3" />;
      case 'down': return <TrendingDown className="h-3 w-3" />;
      default: return null;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {change && (
          <p className={`text-xs ${getChangeColor()} flex items-center gap-1 mt-1`}>
            {getTrendIcon()}
            {change}
            {description && (
              <span className="text-muted-foreground ml-1">{description}</span>
            )}
          </p>
        )}
        {progress !== undefined && (
          <div className="mt-2">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1">{progress}% complete</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface StatisticsProps {
  payments?: any[];
  users?: any[];
  complianceAlerts?: any[];
  analyticsData?: any;
  blockchainNetworks?: any[];
  blockchainContracts?: any[];
}

export const Statistics: React.FC<StatisticsProps> = ({
  payments = [],
  users = [],
  complianceAlerts = [],
  analyticsData,
  blockchainNetworks = [],
  blockchainContracts = []
}) => {
  // Calculate metrics
  const totalVolume = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  const activeUsers = users.filter(user => user.status === 'active').length;
  const pendingPayments = payments.filter(payment => payment.status === 'pending').length;
  const criticalAlerts = complianceAlerts.filter(alert => alert.severity === 'critical').length;
  const connectedNetworks = blockchainNetworks.filter(network => network.status === 'connected').length;
  const deployedContracts = blockchainContracts.length;

  const stats = [
    {
      title: "Total Volume",
      value: `$${totalVolume.toLocaleString()}`,
      change: "+12.5%",
      changeType: "positive" as const,
      description: "vs last month",
      icon: DollarSign,
      trend: "up" as const,
      progress: 85
    },
    {
      title: "Active Users",
      value: activeUsers,
      change: "+8%",
      changeType: "positive" as const,
      description: "user growth",
      icon: Users,
      trend: "up" as const,
      progress: 72
    },
    {
      title: "Pending Payments",
      value: pendingPayments,
      change: "-15%",
      changeType: "negative" as const,
      description: "processing improved",
      icon: CreditCard,
      trend: "down" as const,
      progress: 45
    },
    {
      title: "Critical Alerts",
      value: criticalAlerts,
      change: "+2",
      changeType: "warning" as const,
      description: "requires attention",
      icon: AlertTriangle,
      trend: "up" as const,
      progress: 20
    },
    {
      title: "Connected Networks",
      value: connectedNetworks,
      change: "Stable",
      changeType: "neutral" as const,
      description: "blockchain health",
      icon: Activity,
      trend: "stable" as const,
      progress: 98
    },
    {
      title: "Smart Contracts",
      value: deployedContracts,
      change: "+1",
      changeType: "positive" as const,
      description: "new deployment",
      icon: Shield,
      trend: "up" as const,
      progress: 90
    }
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};

export default Statistics;
